import type { PriceBreakdown } from '@/types/order';

/* ── 다물품 할인 ── */

export interface MultiItemDiscount {
  discountRate: number;
  discountedPrice: number;
}

/**
 * 다물품 할인 계산
 * - 2개: 저렴한 물품 55% 할인
 * - 3개+: 가장 비싼 물품 정가, 두 번째 55% 할인, 나머지 70% 할인
 */
export function calculateMultiItemDiscounts(
  items: { uid: string; basePrice: number }[],
): Map<string, MultiItemDiscount> {
  const result = new Map<string, MultiItemDiscount>();

  if (items.length < 2) {
    items.forEach((item) => {
      result.set(item.uid, { discountRate: 0, discountedPrice: item.basePrice });
    });
    return result;
  }

  // 가격 내림차순 정렬
  const sorted = [...items].sort((a, b) => b.basePrice - a.basePrice);

  if (items.length === 2) {
    result.set(sorted[0].uid, { discountRate: 0, discountedPrice: sorted[0].basePrice });
    result.set(sorted[1].uid, {
      discountRate: 0.55,
      discountedPrice: Math.round(sorted[1].basePrice * 0.45),
    });
  } else {
    sorted.forEach((item, idx) => {
      if (idx === 0) {
        result.set(item.uid, { discountRate: 0, discountedPrice: item.basePrice });
      } else if (idx === 1) {
        result.set(item.uid, {
          discountRate: 0.55,
          discountedPrice: Math.round(item.basePrice * 0.45),
        });
      } else {
        result.set(item.uid, {
          discountRate: 0.7,
          discountedPrice: Math.round(item.basePrice * 0.3),
        });
      }
    });
  }

  return result;
}

/* ── 가격 계산 ── */

interface PriceInput {
  itemBasePrice: number;
  distanceKm: number;
  originHasElevator: boolean;
  originFloor: number;
  destHasElevator: boolean;
  destFloor: number;
}

export function calculatePrice(input: PriceInput): PriceBreakdown {
  const {
    itemBasePrice,
    distanceKm,
    originHasElevator,
    originFloor,
    destHasElevator,
    destFloor,
  } = input;

  // 1. 거리할증: 3km 기본, 초과 1km마다 5%
  const distanceSurchargeRate = Math.max(0, Math.ceil(distanceKm - 3)) * 0.05;

  // 2. 계단할증 (출발지): EV 없으면 0=계단없음(0%), 1층=5%, 2층=10%, ... (지하1층=5%)
  const safeOriginFloor = originFloor <= -2 ? 0 : originFloor; // -99(미선택) 등 방어
  const stairStepsOrigin = safeOriginFloor < 0 ? Math.abs(safeOriginFloor) : Math.max(0, safeOriginFloor);
  const stairSurchargeOriginRate = !originHasElevator
    ? stairStepsOrigin * 0.05
    : 0;

  // 3. 계단할증 (도착지)
  const safeDestFloor = destFloor <= -2 ? 0 : destFloor; // -99(미선택) 등 방어
  const stairStepsDest = safeDestFloor < 0 ? Math.abs(safeDestFloor) : Math.max(0, safeDestFloor);
  const stairSurchargeDestRate = !destHasElevator
    ? stairStepsDest * 0.05
    : 0;

  // 4. 할증 금액 계산
  const distanceSurcharge = Math.round(itemBasePrice * distanceSurchargeRate);
  const stairSurchargeOrigin = Math.round(itemBasePrice * stairSurchargeOriginRate);
  const stairSurchargeDest = Math.round(itemBasePrice * stairSurchargeDestRate);

  // 5. 합산: 물품가 × (1 + 거리) × (1 + 계단출발) × (1 + 계단도착) → 100원 단위 반올림
  const beforeDiscount = Math.round(
    itemBasePrice *
      (1 + distanceSurchargeRate) *
      (1 + stairSurchargeOriginRate) *
      (1 + stairSurchargeDestRate)
  );

  const totalPrice = Math.round(beforeDiscount / 100) * 100;

  return {
    itemBasePrice,
    distanceSurchargeRate,
    distanceSurcharge,
    stairSurchargeOriginRate,
    stairSurchargeOrigin,
    stairSurchargeDestRate,
    stairSurchargeDest,
    totalPrice,
  };
}

/** 유연시간 할인 계산 (수거 오전/오후 각 5%, 최대 2개 = 10%) */
export function calcFlexDiscount(baseTotal: number, flexCount: number): number {
  const clampedCount = Math.min(Math.max(flexCount, 0), 2);
  return Math.round(baseTotal * clampedCount * 0.05);
}

/** 도착시간 지정 할증 (특정 배송 슬롯 선택 시 7%) */
export function calcDeliveryTimeSurcharge(baseTotal: number, hasDesignatedDelivery: boolean): number {
  return hasDesignatedDelivery ? Math.round(baseTotal * 0.07) : 0;
}

/** 최적 슬롯 할인 (경로 최적화 기반, 수거/배송 각각 0~10%) */
export function calcSlotDiscount(baseTotal: number, pickupRate: number, deliveryRate: number): number {
  return Math.round(baseTotal * pickupRate / 100) + Math.round(baseTotal * deliveryRate / 100);
}

/** 최종 결제 금액 (기본 운송비 - 유연할인 - 슬롯할인 + 도착시간 할증, 100원 단위 반올림) */
export function calcFinalPrice(baseTotal: number, flexCount: number, hasDesignatedDelivery: boolean = false, pickupDiscountRate: number = 0, deliveryDiscountRate: number = 0): number {
  return Math.round((baseTotal - calcFlexDiscount(baseTotal, flexCount) - calcSlotDiscount(baseTotal, pickupDiscountRate, deliveryDiscountRate) + calcDeliveryTimeSurcharge(baseTotal, hasDesignatedDelivery)) / 100) * 100;
}

/** @deprecated 하위 호환 */
export function calcDesignatedSurcharge(_baseTotal: number, _slotCount: number): number {
  return 0;
}
